import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useCart } from "../context/CartContext";
import { SEO } from "../components/SEO";
import { toast, Toaster } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  full_name: "",
  email: "",
  phone: "",
  address_line1: "",
  address_line2: "",
  city: "",
  state: "",
  postal_code: "",
  country: "India",
  notes: "",
};

export default function Checkout() {
  const { items, subtotal, clearCart } = useCart();
  const navigate = useNavigate();
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const shipping = subtotal >= 999 || subtotal === 0 ? 0 : 79;
  const total = subtotal + shipping;

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (items.length === 0) {
      toast.error("Your cart is empty.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        items: items.map((i) => ({ slug: i.slug, title: i.title, qty: i.qty, price: i.price })),
      };
      const { data } = await axios.post(`${API}/orders`, payload);
      clearCart();
      navigate(`/order/${data.order_number}`, { state: { order: data } });
    } catch (err) {
      const msg = err?.response?.data?.detail?.[0]?.msg || "Checkout failed. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Checkout failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="py-40 text-center" data-testid="checkout-empty">
        <p className="font-editorial italic text-3xl text-ink-muted mb-8">Your cart is empty.</p>
        <Link to="/books" className="text-crimson link-underline uppercase tracking-[0.24em] text-sm">Browse the catalogue →</Link>
      </div>
    );
  }

  return (
    <div data-testid="checkout-page">
      <SEO title="Checkout" path="/checkout" noindex />
      <Toaster position="top-center" richColors />
      <section className="pt-16 lg:pt-24 pb-8 grain-overlay">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <p className="text-[11px] uppercase tracking-[0.32em] text-crimson mb-6">§ Checkout</p>
          <h1 className="font-editorial text-6xl md:text-8xl leading-[0.9] tracking-tighter text-ink text-balance">
            Complete your <em className="text-crimson not-italic">order.</em>
          </h1>
        </div>
      </section>

      <section className="py-12">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid grid-cols-12 gap-10">
          <form onSubmit={submit} className="col-span-12 lg:col-span-8 space-y-6" data-testid="checkout-form">
            <p className="text-[11px] uppercase tracking-[0.32em] text-crimson">Shipping Address</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Field label="Full name" required>
                <input required value={form.full_name} onChange={(e) => update("full_name", e.target.value)} className={inputCls} data-testid="checkout-full-name" />
              </Field>
              <Field label="Email" required>
                <input required type="email" value={form.email} onChange={(e) => update("email", e.target.value)} className={inputCls} data-testid="checkout-email" />
              </Field>
              <Field label="Phone" required>
                <input required value={form.phone} onChange={(e) => update("phone", e.target.value)} className={inputCls} data-testid="checkout-phone" />
              </Field>
              <Field label="Country" required>
                <input required value={form.country} onChange={(e) => update("country", e.target.value)} className={inputCls} data-testid="checkout-country" />
              </Field>
            </div>
            <Field label="Address line 1" required>
              <input required value={form.address_line1} onChange={(e) => update("address_line1", e.target.value)} className={inputCls} data-testid="checkout-address-1" />
            </Field>
            <Field label="Address line 2">
              <input value={form.address_line2} onChange={(e) => update("address_line2", e.target.value)} className={inputCls} data-testid="checkout-address-2" />
            </Field>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Field label="City" required>
                <input required value={form.city} onChange={(e) => update("city", e.target.value)} className={inputCls} data-testid="checkout-city" />
              </Field>
              <Field label="State" required>
                <input required value={form.state} onChange={(e) => update("state", e.target.value)} className={inputCls} data-testid="checkout-state" />
              </Field>
              <Field label="Postal code" required>
                <input required value={form.postal_code} onChange={(e) => update("postal_code", e.target.value)} className={inputCls} data-testid="checkout-postal" />
              </Field>
            </div>
            <Field label="Order notes (optional)">
              <textarea rows={3} value={form.notes} onChange={(e) => update("notes", e.target.value)} className={inputCls} data-testid="checkout-notes" />
            </Field>
            <button
              type="submit"
              disabled={submitting}
              data-testid="checkout-submit-btn"
              className="w-full bg-crimson text-ivory px-8 py-5 text-sm uppercase tracking-[0.24em] hover:bg-crimson-dark transition-colors duration-500 disabled:opacity-50"
            >
              {submitting ? "Placing order…" : `Place Order — ₹${total} →`}
            </button>
            <p className="text-xs text-ink-muted text-center">
              Payment on delivery for this v1. Online payment gateway coming shortly.
            </p>
          </form>

          <aside className="col-span-12 lg:col-span-4">
            <div className="border border-crimson/20 bg-ivory-soft p-8 sticky top-32" data-testid="checkout-summary">
              <p className="text-[11px] uppercase tracking-[0.32em] text-crimson mb-6">Your Order</p>
              <div className="space-y-4 max-h-80 overflow-y-auto pr-2">
                {items.map((i) => (
                  <div key={i.slug} className="flex gap-4">
                    <img src={i.cover} alt={i.title} className="w-14 aspect-[2/3] object-contain bg-ivory" />
                    <div className="flex-1 min-w-0">
                      <p className="font-editorial text-lg text-ink leading-tight truncate">{i.title}</p>
                      <p className="text-xs text-ink-muted mt-1">Qty {i.qty}</p>
                    </div>
                    <p className="font-editorial text-ink">₹{i.qty * i.price}</p>
                  </div>
                ))}
              </div>
              <hr className="my-6 editorial-rule" />
              <div className="space-y-2 text-ink-soft">
                <div className="flex justify-between text-sm"><span>Subtotal</span><span>₹{subtotal}</span></div>
                <div className="flex justify-between text-sm"><span>Shipping</span><span>{shipping === 0 ? "Free" : `₹${shipping}`}</span></div>
              </div>
              <div className="mt-4 pt-4 border-t border-crimson/15 flex justify-between font-editorial text-2xl text-ink">
                <span>Total</span><span data-testid="checkout-total">₹{total}</span>
              </div>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}

const inputCls = "w-full bg-ivory border border-crimson/20 px-4 py-3 text-sm focus:outline-none focus:border-crimson transition-colors duration-300";
const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="block text-[11px] uppercase tracking-[0.22em] text-ink-muted mb-2">
      {label} {required && <span className="text-crimson">*</span>}
    </span>
    {children}
  </label>
);
